import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Breadcrumbs, Button, IconButton, Link, Paper, Typography } from '@mui/material';
import { ArrowLeft, Check } from 'lucide-react';

import { showSuccessToast } from '@/utils/toast-utils';
import layer1Graphic from '@/assets/images/layer-1.svg';

interface UpgradePlanCard {
  id: string;
  name: string;
  tag: string;
  tagColor?: string;
  subtitle: string;
  price: string;
  cycleLabel: string;
  priceNote?: string;
  features: string[];
}

const ALL_UPGRADE_PLANS: UpgradePlanCard[] = [
  {
    id: 'basic',
    name: 'Basic',
    tag: 'FOR SOLO PRACTITIONERS',
    subtitle: 'Everything you need to get started',
    price: '$ 20',
    cycleLabel: '/mo',
    priceNote: 'Includes ads',
    features: [
      '1 login',
      'Manage up to 35 clients',
      'AI meal plan generation',
      'Client app and CRM included',
      'Email and chatbot support',
      'Manual + automated meal plans',
      'Appointments and tele-health with AI summary',
    ],
  },
  {
    id: 'professional',
    name: 'Professional',
    tag: 'YOUR CURRENT PLAN',
    tagColor: '#F18D31',
    subtitle: 'Built to scale your practice faster',
    price: '$ 30',
    cycleLabel: '/mo',
    features: [
      '1 login (no ads)',
      'Manage up to 50 clients',
      '4 AI meal plans per client / month',
      'AI meal adherence and reports',
      'Client app, CRM and payments',
      'Manual + automated meal plans',
      'Appointments and tele-health with AI summary',
    ],
  },
  {
    id: 'clinic',
    name: 'Clinic',
    tag: 'BUILT FOR TEAMS',
    subtitle: 'Ideal for clinics with multiple practitioners',
    price: '$ 150',
    cycleLabel: '/mo',
    features: [
      '1-5 logins + 1 admin',
      '50 clients per login',
      'Buy additional logins',
      'AI-powered workflows',
      'No ads',
      'Additional login: $30 per login',
      '4 AI meal plans per client / month',
      'Client app, CRM and payments',
    ],
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tag: 'UP TO 50 LOGINS',
    subtitle: 'Full-scale solution for large organizations',
    price: '$ 5,000',
    cycleLabel: '/yr',
    features: [
      '1-50 logins + 1 admin',
      '75 clients per login',
      '10 AI meal plans per client / month',
      'All AI-powered workflows included',
      'No ads',
      'Client app, CRM and payments',
      'Appointments and tele-health with AI summary',
    ],
  },
];

const SCROLL_THUMB_WIDTH_PERCENT = 58;
const PLAN_CARD_WIDTH = 'clamp(320px, 32vw, 420px)';

const UpgradePlanView: React.FC = () => {
  const navigate = useNavigate();
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const plansRowRef = useRef<HTMLDivElement | null>(null);

  const displayedPlans = useMemo(() => {
    if (showAllPlans) {
      return ALL_UPGRADE_PLANS;
    }
    return ALL_UPGRADE_PLANS.filter((planCard) => planCard.id === 'clinic' || planCard.id === 'enterprise');
  }, [showAllPlans]);

  const handleUpgradePlan = (planName: string) => {
    showSuccessToast('Upgrade request started', `${planName} plan selection has been recorded.`);
  };

  const handleViewAllPlans = () => {
    setShowAllPlans(true);
  };

  const updateScrollProgress = useCallback(() => {
    const scrollNode = plansRowRef.current;

    if (!showAllPlans || !scrollNode) {
      setScrollProgress(0);
      return;
    }

    const maxScroll = scrollNode.scrollWidth - scrollNode.clientWidth;
    if (maxScroll <= 0) {
      setScrollProgress(0);
      return;
    }

    const nextProgress = Math.min(1, Math.max(0, scrollNode.scrollLeft / maxScroll));
    setScrollProgress(nextProgress);
  }, [showAllPlans]);

  useEffect(() => {
    updateScrollProgress();

    if (!showAllPlans) return undefined;

    const scrollNode = plansRowRef.current;
    if (!scrollNode) return undefined;

    const handleResize = () => updateScrollProgress();
    const handleScroll = () => updateScrollProgress();

    scrollNode.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      scrollNode.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [showAllPlans, updateScrollProgress, displayedPlans.length]);

  return (
    <Box sx={{ position: 'relative', py: 1, mt: { lg: 1.5 }, ml: { lg: 0.5 } }}>
      <Paper
        elevation={0}
        sx={{
          minHeight: 'calc(100vh - 7.5rem)',
          overflow: 'hidden',
          borderRadius: '28px',
          border: '1px solid #dbe6eb',
          bgcolor: '#f7f9fb',
        }}
      >
        <Box sx={{ px: { xs: 2, sm: 3, md: 4 }, pt: 2.5 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: '14px',
              border: '1px solid #dbe6eb',
              bgcolor: '#f8fafc',
              px: { xs: 2, sm: 3 },
              py: 1.25,
            }}
          >
            <Breadcrumbs separator="/" aria-label="breadcrumb">
              <Link component={RouterLink} to="/profile?view=hub" underline="hover" sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#64748b' }}>
                Settings
              </Link>
              <Typography sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px', color: '#0f172a' }}>
                Upgrade Plan
              </Typography>
            </Breadcrumbs>
          </Paper>
        </Box>

        <Box sx={{ px: { xs: 2, sm: 3, lg: 3.5 }, pb: { xs: 2.5, sm: 3.5 }, pt: { xs: 2, sm: 2.5 } }}>
          <Box
            sx={{
              mb: 2.4,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1.5,
              flexWrap: 'wrap',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75, minWidth: 0 }}>
              <IconButton
                aria-label="Back to subscription plan"
                onClick={() => navigate('/profile?view=subscription-plan')}
                sx={{
                  mt: 0.3,
                  width: 30,
                  height: 30,
                  color: '#1F2B34',
                  p: 0,
                  '&:hover': {
                    bgcolor: 'transparent',
                  },
                }}
              >
                <ArrowLeft size={28} strokeWidth={2.1} />
              </IconButton>

              <Box sx={{ minWidth: 0, pt: 0.05 }}>
                <Typography sx={{ color: '#1F2B34', fontWeight: 700, fontSize: { xs: '1.56rem', sm: '2.02rem' }, lineHeight: 1.05 }}>
                  {showAllPlans ? 'Explore All CalorieScience Plans' : 'Upgrade Your CalorieScience Plan'}
                </Typography>
                <Typography sx={{ mt: 0.65, color: '#676B80', fontSize: { xs: '0.8rem', sm: '0.92rem' }, lineHeight: 1.2 }}>
                  {showAllPlans
                    ? 'Compare plans designed for nutritionists and clinics to choose what fits your practice on CalorieScience.'
                    : 'Choose a plan based on how you want to use CalorieScience.'}
                </Typography>
              </Box>
            </Box>

            <Button
              type="button"
              variant="text"
              onClick={handleViewAllPlans}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                color: '#228ED0',
                fontSize: '1.02rem',
                px: 0.4,
                py: 0.25,
                '&:hover': {
                  bgcolor: 'transparent',
                  color: '#1679B5',
                },
              }}
            >
              View all Plans
            </Button>
          </Box>

          <Box
            ref={plansRowRef}
            sx={{
              ...(showAllPlans
                ? {
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: 1,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    pb: 0.2,
                    pr: 0.3,
                    scrollSnapType: 'x proximity',
                  }
                : {
                    display: 'flex',
                    flexWrap: 'nowrap',
                    gap: 1,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    pb: 0.2,
                    pr: 0.3,
                  }),
            }}
          >
            {displayedPlans.map((planCard) => (
              <Box
                key={planCard.id}
                sx={
                  showAllPlans
                    ? {
                        flex: `0 0 ${PLAN_CARD_WIDTH}`,
                        display: 'flex',
                        scrollSnapAlign: 'start',
                      }
                    : {
                        flex: `0 0 ${PLAN_CARD_WIDTH}`,
                        display: 'flex',
                      }
                }
              >
                <Paper
                  elevation={0}
                  sx={{
                    width: '100%',
                    borderRadius: '14px',
                    border: '1px solid #dbe6eb',
                    bgcolor: '#fff',
                    overflow: 'hidden',
                    boxShadow: '0 2px 6px rgba(15, 23, 42, 0.06)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      position: 'relative',
                      px: { xs: 2.25, sm: 2.8 },
                      py: { xs: 2.2, sm: 2.5 },
                      background: 'linear-gradient(90deg, #22B7DE 0%, #138AB8 100%)',
                    }}
                  >
                    <Box sx={{ position: 'absolute', inset: 0, display: 'flex', justifyContent: 'flex-end', opacity: 0.8 }}>
                      <Box
                        component="img"
                        src={layer1Graphic}
                        alt=""
                        aria-hidden
                        sx={{ width: { xs: '50%', sm: '44%' }, height: '100%', objectFit: 'cover' }}
                      />
                    </Box>

                    <Box sx={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.2 }}>
                      <Box>
                        <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: { xs: '2rem', sm: '2.12rem' }, lineHeight: 1 }}>
                          {planCard.name}
                        </Typography>
                        <Box
                          sx={{
                            mt: 1.35,
                            display: 'inline-flex',
                            borderRadius: '7px',
                            px: 1,
                            py: 0.4,
                            bgcolor: '#fff',
                            color: planCard.tagColor || '#17A3D1',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            letterSpacing: '0.02em',
                            textTransform: 'uppercase',
                          }}
                        >
                          {planCard.tag}
                        </Box>
                        <Typography sx={{ mt: 1.05, color: 'rgba(255,255,255,0.94)', fontSize: '0.92rem' }}>
                          {planCard.subtitle}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-end',
                          color: '#fff',
                          flexShrink: 0,
                          pt: 1.35,
                        }}
                      >
                        <Box sx={{ display: 'inline-flex', alignItems: 'baseline' }}>
                          <Typography sx={{ color: '#fff', fontSize: { xs: '2.1rem', sm: '2.4rem' }, fontWeight: 500, lineHeight: 1 }}>
                            {planCard.price}
                          </Typography>
                          <Typography sx={{ color: '#fff', fontSize: { xs: '1.5rem', sm: '1.85rem' }, lineHeight: 1, ml: 0.12 }}>
                            {planCard.cycleLabel}
                          </Typography>
                        </Box>
                        {planCard.priceNote ? (
                          <Typography sx={{ mt: 0.28, color: '#fff', fontSize: '0.82rem', lineHeight: 1.1, opacity: 0.95 }}>
                            {planCard.priceNote}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ px: { xs: 2.2, sm: 2.8 }, py: { xs: 2.2, sm: 2.5 }, display: 'grid', rowGap: 1.18, flex: 1 }}>
                    {planCard.features.map((featureLabel) => (
                      <Box key={`${planCard.id}-${featureLabel}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <Box
                          sx={{
                            mt: 0.23,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            bgcolor: '#87D4F0',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          <Check size={12} strokeWidth={3} color="#20AEDC" />
                        </Box>
                        <Typography sx={{ color: '#23303A', fontSize: '1.02rem', lineHeight: 1.25 }}>{featureLabel}</Typography>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ px: { xs: 2.2, sm: 2.8 }, pb: { xs: 2.2, sm: 2.5 }, pt: 0.2 }}>
                    <Button
                      type="button"
                      variant="outlined"
                      fullWidth
                      onClick={() => handleUpgradePlan(planCard.name)}
                      sx={{
                        textTransform: 'none',
                        borderRadius: '10px',
                        py: 0.88,
                        color: '#202D36',
                        borderColor: '#CDD7E1',
                        fontWeight: 700,
                        fontSize: '1.04rem',
                        '&:hover': {
                          borderColor: '#B9C6D3',
                          bgcolor: '#F7FAFD',
                        },
                      }}
                    >
                      Upgrade Plan
                    </Button>
                  </Box>
                </Paper>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              width: { xs: 220, sm: 265 },
              height: 8,
              borderRadius: '999px',
              bgcolor: '#D9E0E6',
              mx: 'auto',
              mt: 3.5,
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                left: `${scrollProgress * (100 - SCROLL_THUMB_WIDTH_PERCENT)}%`,
                top: 0,
                width: `${SCROLL_THUMB_WIDTH_PERCENT}%`,
                height: '100%',
                borderRadius: '999px',
                bgcolor: '#25A7D7',
                transition: 'left 120ms linear',
              }}
            />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default UpgradePlanView;
